import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Animated counter component
function AnimatedCount({ target, suffix = '', prefix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    const el = document.getElementById(`counter-${target}-${suffix}`);
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [target, suffix]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return <span id={`counter-${target}-${suffix}`}>{prefix}{count}{suffix}</span>;
}

// Floating particles background
function ParticleField() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-20"
          style={{
            width: `${Math.random() * 6 + 2}px`,
            height: `${Math.random() * 6 + 2}px`,
            background: `rgba(13, 148, 136, ${Math.random() * 0.5 + 0.2})`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function Landing() {
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActiveFeature(f => (f + 1) % 4), 4000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    { icon: '🔍', title: 'AI-Powered Auditing', desc: 'ChartAuditer reads every page — typed and handwritten — catching issues humans miss.' },
    { icon: '📊', title: 'HHCAPS Tracking', desc: 'All 5 quality domains monitored per audit with trend analysis across your agency.' },
    { icon: '⚡', title: 'LUPA Threshold Alerts', desc: 'PDGM visit thresholds tracked by clinical grouping — never miss a LUPA risk.' },
    { icon: '🏥', title: 'Agency Health Score', desc: 'Weakness tracking across audits with auto-generated recommendation reports.' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-hidden">
      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-20px) rotate(5deg); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.5; } 50% { transform: scale(1.1); opacity: 0.2; } 100% { transform: scale(0.9); opacity: 0.5; } }
        @keyframes gradient-shift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .animate-slideUp { animation: slideUp 0.8s ease-out forwards; }
        .animate-fadeIn { animation: fadeIn 1s ease-out forwards; }
        .gradient-text { background: linear-gradient(135deg, #14b8a6, #06b6d4, #0ea5e9); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .glass-dark { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); backdrop-filter: blur(20px); }
        .glow-teal { box-shadow: 0 0 40px rgba(20, 184, 166, 0.15), 0 0 80px rgba(20, 184, 166, 0.05); }
        .hero-gradient { background: radial-gradient(ellipse at 30% 20%, rgba(13, 148, 136, 0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(6, 182, 212, 0.1) 0%, transparent 50%), radial-gradient(ellipse at 50% 50%, rgba(15, 23, 42, 1) 0%, rgba(3, 7, 18, 1) 100%); }
      `}</style>

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center hero-gradient">
        <ParticleField />
        {/* Glowing orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" style={{ animation: 'pulse-ring 8s ease-in-out infinite' }} />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" style={{ animation: 'pulse-ring 10s ease-in-out infinite 2s' }} />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="animate-slideUp">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark mb-8">
              <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              <span className="text-sm text-teal-300 font-medium">AI-Powered Healthcare Compliance</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              <span className="text-white">Automated Chart Audits</span>
              <br />
              <span className="gradient-text">in Under 60 Seconds</span>
            </h1>

            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              ChartAudit AI uses advanced vision AI to read every page of your clinical charts — including handwritten signatures and dates — delivering comprehensive compliance audits for home health and hospice agencies.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl font-semibold text-lg shadow-xl shadow-teal-500/25 hover:shadow-teal-500/40 transition-all hover:scale-105">
                Get Started Free
                <svg className="inline ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </Link>
              <a href="#comparison"
                className="px-8 py-4 rounded-xl font-semibold text-lg glass-dark text-gray-300 hover:text-white hover:border-teal-500/30 transition-all">
                See How It Works
              </a>
            </div>
          </div>

          {/* Floating stat cards */}
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl mx-auto animate-fadeIn" style={{ animationDelay: '0.5s' }}>
            {[
              { value: '60', unit: 'sec', label: 'Average Audit Time' },
              { value: '150', unit: '+', label: 'Pages Per Chart' },
              { value: '99', unit: '%', label: 'Accuracy Rate' },
            ].map((stat, i) => (
              <div key={i} className="glass-dark rounded-xl p-4 glow-teal">
                <div className="text-3xl font-bold gradient-text">
                  <AnimatedCount target={parseInt(stat.value)} suffix={stat.unit} />
                </div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-teal-500/60" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
        </div>
      </section>

      {/* COST & TIME COMPARISON */}
      <section id="comparison" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Agencies Are <span className="gradient-text">Switching to AI</span></h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">Stop paying premium rates for manual audits that take weeks. Get better results in seconds.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Cost Comparison */}
            <div className="glass-dark rounded-2xl p-8 glow-teal">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h3 className="text-xl font-bold">Cost Per Audit</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <div>
                    <p className="text-sm text-gray-400">Traditional Consultant</p>
                    <p className="text-3xl font-bold text-red-400">$200<span className="text-sm font-normal text-gray-500"> / report</span></p>
                  </div>
                  <svg className="w-8 h-8 text-red-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-teal-500/10 border border-teal-500/20">
                  <div>
                    <p className="text-sm text-gray-400">ChartAudit AI</p>
                    <p className="text-3xl font-bold text-teal-400">$50<span className="text-sm font-normal text-gray-500"> / report</span></p>
                  </div>
                  <svg className="w-8 h-8 text-teal-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                </div>
                <div className="text-center pt-2">
                  <span className="text-2xl font-bold text-teal-400">75% Cost Savings</span>
                  <p className="text-xs text-gray-500 mt-1">Save $150 per audit report</p>
                </div>
              </div>
            </div>

            {/* Time Comparison */}
            <div className="glass-dark rounded-2xl p-8 glow-teal">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h3 className="text-xl font-bold">Turnaround Time</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <div>
                    <p className="text-sm text-gray-400">Traditional Consultant</p>
                    <p className="text-3xl font-bold text-red-400">1-2<span className="text-sm font-normal text-gray-500"> weeks</span></p>
                  </div>
                  <svg className="w-8 h-8 text-red-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-teal-500/10 border border-teal-500/20">
                  <div>
                    <p className="text-sm text-gray-400">ChartAudit AI</p>
                    <p className="text-3xl font-bold text-teal-400">~1<span className="text-sm font-normal text-gray-500"> minute</span></p>
                  </div>
                  <svg className="w-8 h-8 text-teal-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                </div>
                <div className="text-center pt-2">
                  <span className="text-2xl font-bold text-cyan-400">10,000x Faster</span>
                  <p className="text-xs text-gray-500 mt-1">From weeks to under a minute</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-transparent via-teal-950/20 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Built for <span className="gradient-text">Home Health & Hospice</span></h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">Every feature designed specifically for the compliance challenges healthcare agencies face daily.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                onMouseEnter={() => setActiveFeature(i)}
                className={`glass-dark rounded-2xl p-6 cursor-pointer transition-all duration-500 ${
                  activeFeature === i ? 'border-teal-500/40 glow-teal scale-[1.02]' : 'hover:border-white/10'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{f.icon}</div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional feature highlights */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: '📝', label: 'Vision OCR', sub: 'Reads handwriting' },
              { icon: '🔒', label: 'HIPAA Compliant', sub: 'AES-256 encrypted' },
              { icon: '📋', label: 'PDF Reports', sub: 'Downloadable' },
              { icon: '📈', label: 'Trend Analysis', sub: 'Multi-audit tracking' },
            ].map((item, i) => (
              <div key={i} className="glass-dark rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="text-sm font-semibold">{item.label}</div>
                <div className="text-xs text-gray-500">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How It <span className="gradient-text">Works</span></h2>
          </div>

          <div className="space-y-8">
            {[
              { step: '01', title: 'Upload Your Chart', desc: 'Upload the patient chart PDF — our system handles documents up to 150+ pages with typed and handwritten content.', color: 'teal' },
              { step: '02', title: 'AI Reads & Analyzes', desc: 'ChartAuditer reads every page, extracts signatures, dates, and clinical data, then audits against CMS compliance standards.', color: 'cyan' },
              { step: '03', title: 'Get Your Report', desc: 'Receive a comprehensive audit report with scored findings, LUPA analysis, HHCAPS measures, and clinician details — all in under a minute.', color: 'sky' },
              { step: '04', title: 'Track Agency Health', desc: 'Weaknesses are tracked across audits. After configurable intervals, get automated recommendation reports to improve compliance.', color: 'blue' },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-6">
                <div className={`flex-shrink-0 w-14 h-14 rounded-2xl bg-${s.color}-500/20 flex items-center justify-center`}>
                  <span className={`text-xl font-bold text-${s.color}-400`}>{s.step}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="glass-dark rounded-3xl p-12 glow-teal relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-cyan-500/5" />
            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-4">Ready to Automate Your <span className="gradient-text">Chart Audits</span>?</h2>
              <p className="text-gray-400 text-lg mb-8">Join healthcare agencies saving thousands per month on compliance auditing.</p>
              <Link to="/register"
                className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl font-semibold text-lg shadow-xl shadow-teal-500/25 hover:shadow-teal-500/40 transition-all hover:scale-105">
                Start Auditing Today
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </Link>
              <p className="text-sm text-gray-500 mt-4">HIPAA compliant with signed BAA during registration</p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img
              src="/chart-auditer-ai-logo.svg"
              alt="Chart Auditer AI logo"
              className="h-10 w-auto object-contain"
            />
          </div>
          <p className="text-xs text-gray-600">This tool is for educational and audit preparation purposes only. It does not guarantee regulatory compliance outcomes.</p>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/login" className="hover:text-teal-400 transition">Login</Link>
            <Link to="/register" className="hover:text-teal-400 transition">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
